import { useState } from 'react';
import { AlertTriangle, Send } from 'lucide-react';
import ModalShell from './ui/ModalShell';
import useJeMobil from '../hooks/useJeMobil';

const BARVA = '#D85A30';

/**
 * Vrácení směny do otevřených — důvod je povinný.
 *
 * Rám (portál do body, zdola na mobilu / na střed na desktopu, sklo, z-index
 * nad lištou, Esc, zámek scrollu) drží `ui/ModalShell`. Dřív si tenhle modal
 * kreslil `position: fixed` sám uvnitř stromu — a protože ho volá kalendář
 * v „Můj měsíc", jehož karta má `backdrop-filter`, vázal se fixed k té kartě:
 * modal se vykreslil uvnitř dlaždice, ořízl ho její `overflow: hidden`
 * a zůstal pod sheetem detailu dne. Vypadalo to, že se nic neotevřelo.
 *
 * Props:
 *   smena     — { id, datum, cas_od, cas_do, sablon_nazev }
 *   onClose   — zavření
 *   onSuccess — po úspěšném vrácení (refresh dat volajícího)
 *   isDark    — tmavé téma
 */
export default function VratitSmenuModal({ smena, onClose, onSuccess, isDark }) {
    const isMobile = useJeMobil();
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const [duvod, setDuvod] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const lzeOdeslat = duvod.trim().length >= 3 && !loading;

    const handleSubmit = async () => {
        const trimmed = duvod.trim();
        if (trimmed.length < 3) {
            setError('Důvod musí mít alespoň 3 znaky.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`/api/shifts/${smena.id}/vratit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrf(),
                },
                body: JSON.stringify({ duvod: trimmed }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setError(body.error || 'Vrácení selhalo.');
                setLoading(false);
                return;
            }
            onSuccess?.();
            onClose();
        } catch {
            setError('Chyba připojení.');
            setLoading(false);
        }
    };

    const fmtTime = (t) => t ? String(t).substring(0, 5) : '—';
    const datumStr = typeof smena.datum === 'string' ? smena.datum.substring(0, 10) : smena.datum;
    const datumFmt = new Date(datumStr + 'T12:00:00').toLocaleDateString('cs-CZ', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });

    const paticka = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: mutedColor, flexShrink: 0 }}>{duvod.length} / 500</span>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={onClose} disabled={loading} style={{
                minHeight: isMobile ? 44 : 38, padding: '0 16px', borderRadius: 8,
                border: `1px solid ${borderColor}`, background: 'transparent',
                color: secondaryColor, fontSize: 13, cursor: 'pointer',
            }}>Zrušit</button>
            <button type="button" onClick={handleSubmit} disabled={!lzeOdeslat} style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                minHeight: isMobile ? 44 : 38, padding: '0 16px', borderRadius: 8,
                border: 'none', background: BARVA, color: '#fff',
                fontSize: 13, fontWeight: 600, cursor: lzeOdeslat ? 'pointer' : 'not-allowed',
                opacity: lzeOdeslat ? 1 : 0.5,
            }}>
                <Send size={13} />
                {loading ? 'Odesílám…' : 'Vrátit směnu'}
            </button>
        </div>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Vrátit směnu"
            ikona={AlertTriangle} barva={BARVA} sirka={480}
            zavritLze={!loading} paticka={paticka}>
            <div style={{ padding: isMobile ? 16 : '16px 16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {smena.sablon_nazev || 'Směna'}
                    </div>
                    <div style={{ fontSize: 12.5, color: secondaryColor, marginTop: 2 }}>
                        {datumFmt} · {fmtTime(smena.cas_od)}–{fmtTime(smena.cas_do)}
                    </div>
                </div>

                <div style={{
                    padding: '9px 11px', borderRadius: 8, fontSize: 12, lineHeight: 1.45,
                    background: `${BARVA}14`, border: `1px solid ${BARVA}40`, color: secondaryColor,
                }}>
                    Směna se převede do otevřených, ostatní kvalifikovaní dostanou notifikaci.
                </div>

                <div>
                    <label htmlFor="vratit-duvod" style={{
                        display: 'block', marginBottom: 6,
                        fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
                        textTransform: 'uppercase', color: mutedColor,
                    }}>Důvod (povinné)</label>
                    <textarea
                        id="vratit-duvod"
                        value={duvod}
                        onChange={(e) => { setDuvod(e.target.value); setError(null); }}
                        maxLength={500}
                        rows={4}
                        autoFocus={!isMobile}
                        placeholder="Např. rodinná událost, nemoc, plánovaná návštěva lékaře…"
                        style={{
                            display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
                            resize: 'none',
                            border: `1px solid ${error ? BARVA : borderColor}`,
                            borderRadius: 8, padding: '10px 12px',
                            // Pod 16 px iOS při fokusu stránku přizoomuje a rozvržení se rozjede.
                            fontSize: 16, lineHeight: 1.4, color: 'var(--text-primary)',
                            background: isDark ? 'rgba(255,255,255,.05)' : 'var(--surface-base)',
                            fontFamily: 'inherit', outline: 'none',
                        }}
                    />
                </div>

                {error && (
                    <div style={{
                        padding: '8px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.4,
                        background: 'rgba(216,90,48,.12)', border: '1px solid rgba(216,90,48,.3)', color: BARVA,
                    }}>{error}</div>
                )}
            </div>
        </ModalShell>
    );
}
